import axios, { type AxiosError, type AxiosInstance } from 'axios';

export interface ProblemDetails {
    title?: string;
    detail?: string;
    errors?: Record<string, string[]>;
}

export const http: AxiosInstance = axios.create({
    baseURL: '/api',
    withCredentials: true,
    headers: {
        Accept: 'application/json'
    },
    timeout: 30_000
});

http.interceptors.response.use(
    (r) => r,
    (err: AxiosError<ProblemDetails>) => {
        // 401: пользователь разлогинен. Редирект на /login делаем не здесь
        // (interceptor не должен ничего знать про роутер) — auth-store сам
        // среагирует, поймав ApiError. Throw, чтобы вызывающий код узнал.
        return Promise.reject(toApiError(err));
    }
);

export class ApiError extends Error {
    public readonly status: number | undefined;
    public readonly problem: ProblemDetails | undefined;
    public readonly fieldErrors: Record<string, string[]> | undefined;

    constructor(message: string, opts: { status?: number; problem?: ProblemDetails }) {
        super(message);
        this.name = 'ApiError';
        this.status = opts.status;
        this.problem = opts.problem;
        this.fieldErrors = opts.problem?.errors;
    }

    isUnauthorized(): boolean {
        return this.status === 401;
    }

    isForbidden(): boolean {
        return this.status === 403;
    }

    isValidation(): boolean {
        return this.status === 400 && !!this.fieldErrors;
    }
}

function toApiError(err: AxiosError<ProblemDetails>): ApiError {
    if (err.response) {
        const problem = err.response.data;
        const message =
            (problem && (problem.detail || problem.title)) ||
            err.response.statusText ||
            'Запрос не выполнен';
        return new ApiError(message, { status: err.response.status, problem });
    }
    return new ApiError(err.message || 'Сетевая ошибка', {});
}
